import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from '../traderhub24-logo.svg'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import { sendTradePoolNotificationEmail } from './lib/email'
import Login from './components/Login'
import Register from './components/Register'

type PageKey =
  | 'home'
  | 'changelog'
  | 'privacy-policy'
  | 'terms-and-conditions'
  | 'risk-warning'
  | 'documentation'
  | 'all-trades'
  | 'analytics'
  | 'tools'
  | 'strategies'
  | 'account'
  | 'symbol-management'
  | 'social-trade'
  | 'social-trade-trade-pools'
  | 'social-trade-followers'

type TradePoolRow = {
  id: string
  name: string
  settings: any
  created_at: string
  updated_at: string
}

type PublicTradeRow = {
  id: string
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  long_short: 'long' | 'short'
  entry_date: string | null
  exit_date: string | null
  entry_price: number | null
  exit_price: number | null
  pnl: number | null
  currency: string | null
  trade_pool: string | null
  public_slug: string | null
}

type FollowerRow = {
  id: string
  email: string
  scope: 'global' | 'pool'
  trade_pool_id: string | null
  created_at: string
}

function isValidEmail(email: string) {
  // pragmatic validation for UI; DB enforces uniqueness + normalization
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function getAddNewFollowersAutomatically(pool: TradePoolRow) {
  return Boolean(pool?.settings?.addNewFollowersAutomatically)
}

function getIsRestrictedAccess(pool: TradePoolRow) {
  return Boolean(pool?.settings?.isRestrictedAccess)
}

function TradePools({
  onBack,
  setCurrentPage,
  embedded,
}: {
  onBack: () => void
  setCurrentPage: (page: PageKey) => void
  embedded?: boolean
}) {
  const navigate = useNavigate()
  const { user, signOut, loading: authLoading } = useAuth()
  const { isFree, profileLoading, canCreatePools } = useMembership()
  const { showError, showConfirm } = useNotification()

  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pools, setPools] = useState<TradePoolRow[]>([])
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null)

  const [newPoolName, setNewPoolName] = useState('')
  const [creatingPool, setCreatingPool] = useState(false)

  const [renamingPoolId, setRenamingPoolId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const [publicTrades, setPublicTrades] = useState<PublicTradeRow[]>([])
  const [loadingTrades, setLoadingTrades] = useState(false)

  const [poolFollowers, setPoolFollowers] = useState<FollowerRow[]>([])
  const [loadingFollowers, setLoadingFollowers] = useState(false)
  const [newFollowerEmail, setNewFollowerEmail] = useState('')

  const selectedPool = useMemo(() => pools.find((p) => p.id === selectedPoolId) || null, [pools, selectedPoolId])

  // Session control - redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) setShowLogin(true)
  }, [authLoading, user])

  const fetchPools = async () => {
    if (!user) return
    try {
      setLoading(true)
      const { data, error: qError } = await supabase
        .from('trade_pools')
        .select('id, name, settings, created_at, updated_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      if (qError) throw qError
      setPools((data as TradePoolRow[]) || [])
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load trade pools')
    } finally {
      setLoading(false)
    }
  }

  const fetchPublicTrades = async () => {
    if (!user) return
    try {
      setLoadingTrades(true)
      const { data, error: qError } = await supabase
        .from('trades_with_market_data')
        .select('id, symbol, market, long_short, entry_date, exit_date, entry_price, exit_price, pnl, currency, trade_pool, public_slug, public_visibility')
        .eq('user_id', user.id)
        .eq('is_public', true)
        .order('entry_date', { ascending: false })
      if (qError) throw qError
      setPublicTrades((data as PublicTradeRow[]) || [])
    } catch (err: any) {
      showError(err.message || 'Failed to load trades')
    } finally {
      setLoadingTrades(false)
    }
  }

  const fetchPoolFollowers = async (poolId: string) => {
    if (!user) return
    try {
      setLoadingFollowers(true)
      const { data, error: qError } = await supabase
        .from('followers')
        .select('id, email, scope, trade_pool_id, created_at')
        .eq('user_id', user.id)
        .eq('scope', 'pool')
        .eq('trade_pool_id', poolId)
        .order('created_at', { ascending: false })
      if (qError) throw qError
      setPoolFollowers((data as FollowerRow[]) || [])
    } catch (err: any) {
      showError(err.message || 'Failed to load pool followers')
    } finally {
      setLoadingFollowers(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchPools()
      fetchPublicTrades()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (selectedPoolId) fetchPoolFollowers(selectedPoolId)
    else setPoolFollowers([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoolId])

  const handleUpgrade = () => {
    setCurrentPage('home')
    setTimeout(() => {
      const el = document.getElementById('pricing')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const createPool = async () => {
    if (!user) return
    const name = newPoolName.trim()
    if (!name) return
    try {
      setCreatingPool(true)
      const { error: iError } = await supabase.from('trade_pools').insert({
        user_id: user.id,
        name,
        settings: { showPnl: true, showQuantityDefault: false, addNewFollowersAutomatically: false, isRestrictedAccess: false },
      })
      if (iError) throw iError
      setNewPoolName('')
      await fetchPools()
    } catch (err: any) {
      showError(err.message || 'Failed to create pool')
    } finally {
      setCreatingPool(false)
    }
  }

  const setPoolOptionAddNewFollowersAutomatically = async (pool: TradePoolRow, enabled: boolean) => {
    try {
      const nextSettings = { ...(pool.settings || {}), addNewFollowersAutomatically: enabled }
      const { error: uError } = await supabase.from('trade_pools').update({ settings: nextSettings }).eq('id', pool.id)
      if (uError) throw uError
      setPools((prev) => prev.map((p) => (p.id === pool.id ? { ...p, settings: nextSettings } : p)))
    } catch (err: any) {
      showError(err.message || 'Failed to update pool options')
    }
  }

  const setPoolOptionRestrictAccess = async (pool: TradePoolRow, enabled: boolean) => {
    try {
      const nextSettings = { ...(pool.settings || {}), isRestrictedAccess: enabled }
      const { error: uError } = await supabase.from('trade_pools').update({ settings: nextSettings }).eq('id', pool.id)
      if (uError) throw uError
      setPools((prev) => prev.map((p) => (p.id === pool.id ? { ...p, settings: nextSettings } : p)))

      // If enabling restricted access, update all trades in this pool to restricted
      if (enabled) {
        const { error: updateError } = await supabase
          .from('trades')
          .update({ public_visibility: 'restricted' })
          .eq('user_id', user!.id)
          .eq('trade_pool', pool.id)
          .eq('is_public', true)
        if (updateError) {
          console.warn('Failed to update existing trades to restricted:', updateError)
          // Don't throw - setting is saved, just existing trades might not be updated
        }
        await fetchPublicTrades()
      }
    } catch (err: any) {
      showError(err.message || 'Failed to update pool options')
    }
  }

  const startRename = (pool: TradePoolRow) => {
    setRenamingPoolId(pool.id)
    setRenameValue(pool.name)
  }

  const saveRename = async () => {
    if (!user || !renamingPoolId) return
    const name = renameValue.trim()
    if (!name) return
    try {
      const { error: uError } = await supabase.from('trade_pools').update({ name }).eq('id', renamingPoolId)
      if (uError) throw uError
      setRenamingPoolId(null)
      setRenameValue('')
      await fetchPools()
    } catch (err: any) {
      showError(err.message || 'Failed to rename pool')
    }
  }

  const deletePool = async (poolId: string) => {
    const confirmed = await showConfirm({
      title: 'Delete Trade Pool',
      message: 'Delete this Trade Pool? Trades will be unassigned from the pool.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger'
    })
    if (!confirmed) return
    try {
      const { error: dError } = await supabase.from('trade_pools').delete().eq('id', poolId)
      if (dError) throw dError
      if (selectedPoolId === poolId) setSelectedPoolId(null)
      await fetchPools()
      await fetchPublicTrades()
    } catch (err: any) {
      showError(err.message || 'Failed to delete pool')
    }
  }

  const assignTradeToPool = async (tradeId: string, poolId: string | null) => {
    try {
      // If adding to a pool, check if pool has restricted access enabled
      let updateData: { trade_pool: string | null; public_visibility?: string } = { trade_pool: poolId }
      
      if (poolId) {
        const pool = pools.find((p) => p.id === poolId)
        if (pool && getIsRestrictedAccess(pool)) {
          // Pool has restricted access enabled - automatically set trade to restricted
          updateData.public_visibility = 'restricted'
        }
      }
      // Note: When removing from pool (poolId = null), we don't change visibility
      // User can manually change it if needed

      // Update trade pool assignment (and visibility if needed)
      const { error: uError } = await supabase.from('trades').update(updateData).eq('id', tradeId)
      if (uError) throw uError
      
      // Update local state
      setPublicTrades((prev) => prev.map((t) => (t.id === tradeId ? { ...t, trade_pool: poolId } : t)))
      
      // Send email notifications to pool followers (only when adding to pool, not removing)
      if (poolId && user) {
        await sendTradeNotificationToFollowers(tradeId, poolId)
      }
    } catch (err: any) {
      showError(err.message || 'Failed to update trade pool')
    }
  }

  const sendTradeNotificationToFollowers = async (tradeId: string, poolId: string) => {
    try {
      console.log('📧 Starting trade pool notification for trade:', tradeId, 'pool:', poolId)
      
      // Get pool followers
      const { data: followersData, error: followersError } = await supabase
        .from('followers')
        .select('email')
        .eq('user_id', user!.id)
        .eq('scope', 'pool')
        .eq('trade_pool_id', poolId)
      
      if (followersError) {
        console.error('❌ Error fetching followers:', followersError)
        throw followersError
      }
      
      if (!followersData || followersData.length === 0) {
        console.log('⚠️ No followers to notify for this pool')
        return
      }
      
      console.log(`✅ Found ${followersData.length} follower(s) to notify`)

      // Get trade details - fetch fresh from DB to ensure we have all data
      const { data: tradeData, error: tradeError } = await supabase
        .from('trades')
        .select('symbol, market, long_short, entry_date, exit_date, entry_price, exit_price, pnl, currency, public_slug')
        .eq('id', tradeId)
        .single()

      if (tradeError || !tradeData) {
        console.error('Trade not found:', tradeError)
        return
      }

      // Get pool name
      const pool = pools.find(p => p.id === poolId)
      const poolName = pool?.name || 'Trade Pool'

      // Get user profile for trader name
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('user_id', user!.id)
        .single()

      const traderName = profileData?.full_name || user!.email || 'Trader'

      // Build trade URL
      const tradeUrl = tradeData.public_slug 
        ? `${window.location.origin}/p/${tradeData.public_slug}`
        : `${window.location.origin}/trades`

      // Format P&L
      const pnlFormatted = tradeData.pnl !== null && tradeData.pnl !== undefined
        ? `${tradeData.pnl >= 0 ? '+' : ''}${tradeData.currency || '$'}${tradeData.pnl.toFixed(2)}`
        : undefined

      // Format prices
      const entryPriceFormatted = tradeData.entry_price 
        ? `${tradeData.currency || '$'}${tradeData.entry_price.toFixed(2)}`
        : undefined
      const exitPriceFormatted = tradeData.exit_price 
        ? `${tradeData.currency || '$'}${tradeData.exit_price.toFixed(2)}`
        : undefined

      // Prepare email data for trade pool notification
      const emailData = {
        poolName,
        traderName,
        symbol: tradeData.symbol,
        tradeType: tradeData.long_short === 'long' ? 'Long' : 'Short',
        market: tradeData.market,
        entryPrice: entryPriceFormatted,
        exitPrice: exitPriceFormatted,
        pnl: pnlFormatted,
        date: tradeData.entry_date 
          ? new Date(tradeData.entry_date).toLocaleDateString() 
          : new Date().toLocaleDateString(),
        tradeUrl
      }

      // Send emails to all followers (in parallel)
      console.log('📤 Sending emails to followers...')
      const emailPromises = followersData.map(async (follower) => {
        try {
          console.log(`  → Sending email to: ${follower.email}`)
          const result = await sendTradePoolNotificationEmail(follower.email, emailData, follower.email)
          console.log(`  ✅ Email sent successfully to: ${follower.email}`, result)
          return result
        } catch (error) {
          console.error(`  ❌ Failed to send email to ${follower.email}:`, error)
          // Don't throw - continue with other emails
          return null
        }
      })

      const results = await Promise.all(emailPromises)
      const successCount = results.filter(r => r?.success).length
      console.log(`✅ Sent trade pool notifications: ${successCount}/${followersData.length} successful`)
    } catch (err: any) {
      console.error('❌ Error sending trade pool notifications:', err)
      // Don't alert user - this is a background operation
    }
  }

  const addFollowerToPool = async () => {
    if (!user || !selectedPoolId) return
    const email = newFollowerEmail.trim().toLowerCase()
    if (!email) return
    if (!isValidEmail(email)) {
      showError('Invalid email')
      return
    }
    try {
      const { error: iError } = await supabase.from('followers').insert({
        user_id: user.id,
        email,
        scope: 'pool',
        trade_pool_id: selectedPoolId,
      })
      if (iError) throw iError
      setNewFollowerEmail('')
      await fetchPoolFollowers(selectedPoolId)
    } catch (err: any) {
      showError(err.message || 'Failed to add follower')
    }
  }

  const removeFollower = async (id: string) => {
    const confirmed = await showConfirm({
      title: 'Remove Follower',
      message: 'Remove this follower from the pool?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger'
    })
    if (!confirmed) return
    try {
      const { error: dError } = await supabase.from('followers').delete().eq('id', id)
      if (dError) throw dError
      if (selectedPoolId) await fetchPoolFollowers(selectedPoolId)
    } catch (err: any) {
      showError(err.message || 'Failed to remove follower')
    }
  }

  // If not authenticated, show login prompt (skip when embedded; layout handles auth)
  if (!user && !authLoading && !embedded) {
    return (
      <div className="min-h-screen bg-app-bg text-gray-900">
        <header className="w-full flex justify-between items-center py-6 px-6 max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <img
              src={logo}
              alt="TraderHub24"
              className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onBack}
            />
          </div>
          <div className="flex items-center gap-2" />
        </header>

        <main className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center mb-4">Please sign in to access Trade Pools</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setShowLogin(true)}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowRegister(true)}
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>

        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSwitchToRegister={() => {
              setShowLogin(false)
              setShowRegister(true)
            }}
          />
        )}
        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSwitchToLogin={() => {
              setShowRegister(false)
              setShowLogin(true)
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "dashboard-page-content" : "min-h-screen bg-app-bg text-gray-900"}>
      {!embedded && (
      <header className="w-full py-3 px-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <img
            src={logo}
            alt="TraderHub24"
            className="h-20 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/app/social-trade")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              Back to Social Trade
            </button>
            <button
              type="button"
              onClick={() => signOut()}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
      )}

      {(() => {
        const W = embedded ? "div" : "main";
        return (
        <W className={embedded ? "w-full px-6 py-6" : "max-w-6xl mx-auto px-6 py-6"}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Trade Pools</h1>
          <p className="text-gray-600 mt-2">Group trades into pools and manage pool-specific followers.</p>
        </div>

        {!canCreatePools && !profileLoading && (
          <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg text-sm">
              {isFree ? 'Bu özelliği aktifleştirmek için Pro abone olun.' : 'Bu özellik üyeliğiniz için kapalı.'}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleUpgrade}
                className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
              >
                Planları gör
              </button>
              <button
                type="button"
                onClick={() => navigate("/app/social-trade")}
                className="px-5 py-2 bg-app-box border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Social Trade’e dön
              </button>
            </div>
          </div>
        )}

        {canCreatePools && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900">My pools</h2>
              <div className="mt-4 flex gap-2">
                <input
                  value={newPoolName}
                  onChange={(e) => setNewPoolName(e.target.value)}
                  placeholder="New pool name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={createPool}
                  disabled={creatingPool || !newPoolName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingPool ? 'Creating...' : 'Create'}
                </button>
              </div>

              {loading ? (
                <p className="text-gray-500 text-center py-8">Loading pools...</p>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4">{error}</div>
              ) : pools.length === 0 ? (
                <p className="text-gray-500 text-sm mt-4">No pools yet.</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {pools.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPoolId(p.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                        selectedPoolId === p.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 bg-white hover:border-orange-500'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-sm text-gray-900">{p.name}</div>
                        <span className="text-xs text-gray-500">Pool</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="lg:col-span-2 bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
              {!selectedPool ? (
                <div className="text-gray-600 text-sm">Select a pool to manage trades & followers.</div>
              ) : (
                <div className="space-y-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{selectedPool.name}</h2>
                      <p className="text-sm text-gray-600 mt-1">Pool settings are stored, more controls coming soon.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {renamingPoolId === selectedPool.id ? (
                        <>
                          <input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <button
                            type="button"
                            onClick={saveRename}
                            disabled={!renameValue.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRenamingPoolId(null)
                              setRenameValue('')
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => startRename(selectedPool)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            onClick={() => deletePool(selectedPool.id)}
                            className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Options (per selected pool) */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                    <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Options</div>
                    
                    <label className="flex items-center justify-between gap-3 select-none">
                      <div>
                        <div className="text-sm font-medium text-gray-900">Add new followers automatically</div>
                        <div className="text-xs text-gray-500 mt-1">
                          When enabled, any follower you add in the Followers page will also be added to this pool.
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-orange-500"
                        checked={getAddNewFollowersAutomatically(selectedPool)}
                        onChange={(e) => setPoolOptionAddNewFollowersAutomatically(selectedPool, e.target.checked)}
                      />
                    </label>

                    <div className="pt-3 border-t border-gray-200">
                      <label className="flex items-center justify-between gap-3 select-none">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Restrict access to pool followers only</div>
                          <div className="text-xs text-gray-500 mt-1">
                            When enabled, trades added to this pool will automatically be set to "restricted" visibility. Only pool followers will be able to access these trades.
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-orange-500"
                          checked={getIsRestrictedAccess(selectedPool)}
                          onChange={(e) => setPoolOptionRestrictAccess(selectedPool, e.target.checked)}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">Pool followers</h3>
                      {loadingFollowers && <span className="text-xs text-gray-500">Loading...</span>}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <input
                        value={newFollowerEmail}
                        onChange={(e) => setNewFollowerEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <button
                        type="button"
                        onClick={addFollowerToPool}
                        disabled={!newFollowerEmail.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>

                    {poolFollowers.length === 0 ? (
                      <p className="text-xs text-gray-500 mt-3">No pool followers yet.</p>
                    ) : (
                      <ul className="mt-3 divide-y divide-gray-200">
                        {poolFollowers.map((f) => (
                          <li key={f.id} className="py-2 flex items-center justify-between gap-3">
                            <span className="text-sm text-gray-800">{f.email}</span>
                            <button
                              type="button"
                              onClick={() => removeFollower(f.id)}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">Assign public trades to this pool</h3>
                      {loadingTrades && <span className="text-xs text-gray-500">Loading...</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      (MVP) Only trades with <strong>Public share</strong> enabled are shown here.
                    </p>

                    {publicTrades.length === 0 ? (
                      <p className="text-sm text-gray-600 mt-3">No public trades found.</p>
                    ) : (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Symbol
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Market
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Pool
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {publicTrades.map((t) => {
                              const isInThisPool = t.trade_pool === selectedPool.id
                              return (
                                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="font-semibold text-sm text-gray-900">{t.symbol}</div>
                                    <div className="text-xs text-gray-500">{t.exit_date ? 'CLOSED' : 'OPEN'}</div>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{t.market}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-center text-xs">
                                    {isInThisPool ? (
                                      <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">
                                        {selectedPool.name}
                                      </span>
                                    ) : t.trade_pool ? (
                                      <span className="text-gray-500">Other</span>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      {!isInThisPool ? (
                                        <button
                                          type="button"
                                          onClick={() => assignTradeToPool(t.id, selectedPool.id)}
                                          className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                                        >
                                          Add
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => assignTradeToPool(t.id, null)}
                                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {canCreatePools && (
          <section className="mt-8 bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900">How Trade Pools work</h2>
            <p className="text-sm text-gray-600 mt-2">
              Trade Pools help you organize your shared trades into groups and optionally maintain pool-specific follower lists.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-gray-700 list-disc pl-5">
              <li>
                <strong>Trades</strong> can belong to <strong>one pool</strong> at a time. Removing a trade from a pool sets it back to “no pool”.
              </li>
              <li>
                <strong>Followers</strong> are stored as emails. They can be <strong>global</strong> (Followers page) or{' '}
                <strong>pool-specific</strong> (added inside a pool).
              </li>
              <li>
                When you add a trade to a pool, pool's followers will automatically get notified about the trade.
              </li>
              <li>
                If <strong>“Add new followers automatically”</strong> is enabled for a pool, every time you add/import a follower in the{' '}
                Followers page, that email is also added to this pool automatically.
              </li>
              <li>
                If <strong>“Restrict access to pool followers only”</strong> is enabled, trades added to this pool will automatically{' '}
                be set to <strong>restricted</strong> visibility. Only followers who are specifically added to this pool will be able to{' '}
                access these trades (via the Community feed or direct trade links).
              </li>
              <li>
                Pools are private to your account, so each user only sees and manages their own pools,
                trades, and followers.
              </li>
            </ul>
          </section>
        )}
        </W>
        );
      })()}
    </div>
  )
}

export default TradePools

